import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/layout/Layout';

import Dashboard from './pages/Dashboard';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import LeadsPage from './pages/LeadsPage';
import LeadsDashboardPage from './pages/LeadsDashboardPage';
import LeadDetailPage from './pages/LeadDetailPage';
import ProductsPage from './pages/ProductsPage';
import PriceListsPage from './pages/PriceListsPage';
import CalendarPage from './pages/CalendarPage';
import ActivitiesPage from './pages/ActivitiesPage';

import InvoicesPage from './pages/InvoicesPage';
import PassiveInvoicesPage from './pages/PassiveInvoicesPage';
import CreditNotesPage from './pages/CreditNotesPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import AnalisiAziendaliPage from './pages/AnalisiAziendaliPage';
import CommissionsPage from './pages/CommissionsPage';
import NotificationsPage from './pages/NotificationsPage';
import UserManagementPage from './pages/UserManagementPage';
import { DocumentConfigurationPage } from './pages/DocumentConfigurationPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

import { ProtectedRoute } from './components/auth/ProtectedRoute';

import Toast from './components/Toast';
import { LoadingFallback } from './components/LoadingFallback';
import { ErrorBoundary } from './components/ErrorBoundary';
import { UserRole } from './types/roles';
import { useVersionCheck } from './hooks/useVersionCheck';
import { ReloadPrompt } from './components/ReloadPrompt';

function App() {
  const { user, profile, loading, error } = useAuth();
  const [bootBypass, setBootBypass] = useState(false);

  // Controlla periodicamente se c'è una nuova versione disponibile
  useVersionCheck();

  const renderWithRoles = (roles: UserRole[], element: JSX.Element) => (
    <ProtectedRoute requiredRole={roles}>{element}</ProtectedRoute>
  );

  // If loading persists unusually long, bypass as logged-out to avoid infinite spinner
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setBootBypass(true), 1500);
    return () => clearTimeout(t);
  }, [loading]);

  // Remove verbose console logs for production readiness

  // Show loading only for a brief moment, then show login if no user
  if (loading && !bootBypass) {
    return <LoadingFallback message="Caricamento applicazione..." />;
  }

  // If there's an auth error, always show login page
  if (error) {
    // Optionally, show a toast via global store; avoid console noise
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Always make auth routes available */}
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

          {!user ? (
            // Redirect all other routes to login when not authenticated
            <>
              <Route path="*" element={<Navigate to="/auth/login" replace />} />
            </>
          ) : (
            // Protected routes when authenticated (non-production users)
            <>

              {/* Main app routes */}
              <Route path="/" element={<Layout />}>
                <Route
                  index
                  element={
                    profile?.role === 'lab'
                      ? <Navigate to="/lab" replace />
                      : renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione', 'lettore'], <Dashboard />)
                  }
                />
                <Route path="customers" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione', 'lab'], <CustomersPage />)} />
                <Route path="customers/:id" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione', 'lab'], <CustomerDetailPage />)} />
                <Route path="leads" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <LeadsPage />)} />
                <Route path="leads/stats" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <LeadsDashboardPage />)} />
                <Route path="leads/:id" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <LeadDetailPage />)} />
                <Route path="products" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione', 'lettore', 'lab'], <ProductsPage />)} />
                <Route path="price-lists" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <PriceListsPage />)} />
                <Route path="invoices" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <InvoicesPage />)} />
                <Route path="credit-notes" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <CreditNotesPage />)} />
                <Route path="passive-invoices" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <PassiveInvoicesPage />)} />
                <Route path="subscriptions" element={renderWithRoles(['admin', 'commerciale', 'amministrazione', 'sales'], <SubscriptionsPage />)} />
                <Route path="analisi-aziendali" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <AnalisiAziendaliPage />)} />
                <Route path="activities" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione', 'lab'], <ActivitiesPage />)} />
                <Route path="commissions" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <CommissionsPage />)} />
                <Route path="calendar" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione', 'lab'], <CalendarPage />)} />

                <Route path="notifications" element={renderWithRoles(['admin', 'sales', 'commerciale', 'lettore'], <NotificationsPage />)} />
                <Route
                  path="user-management"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <UserManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="document-configuration"
                  element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <DocumentConfigurationPage />)}
                />
              </Route>

              {/* Redirect auth routes to dashboard when already logged in */}
              <Route path="/auth/*" element={<Navigate to="/" replace />} />

              {/* Catch-all route for authenticated users */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
      <Toast />
      <ReloadPrompt />
    </ErrorBoundary>
  );
}

export default App;